import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { format, addDays, subDays, parseISO } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import TimeInput from '../components/TimeInput';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  ChevronLeft,
  ChevronRight,
  Calendar,
  Clock,
  MapPin,
  Truck,
  Home,
  Play,
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  Navigation,
  Loader2,
  Plus,
  Trash2,
  Send,
  Timer,
  WifiOff,
  Edit3
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Event type icons
const EVENT_ICONS = {
  'START_DAY': Play,
  'LEAVE_SHOP': Truck,
  'ARRIVE_JOB': MapPin,
  'START_JOB': Play,
  'FINISH_JOB': CheckCircle,
  'LEAVE_JOB': Navigation,
  'TRAVEL_TO_NEXT': Truck,
  'RETURN_SHOP': Home,
  'END_DAY': CheckCircle
};

// Event type colors
const EVENT_COLORS = {
  'START_DAY': 'bg-green-500',
  'LEAVE_SHOP': 'bg-amber-500',
  'ARRIVE_JOB': 'bg-blue-500',
  'START_JOB': 'bg-green-500',
  'FINISH_JOB': 'bg-purple-500',
  'LEAVE_JOB': 'bg-orange-500',
  'TRAVEL_TO_NEXT': 'bg-amber-500',
  'RETURN_SHOP': 'bg-slate-500',
  'END_DAY': 'bg-slate-700'
};

// Event type friendly labels
const EVENT_LABELS = {
  'START_DAY': 'Started Day',
  'LEAVE_SHOP': 'Left Shop',
  'ARRIVE_JOB': 'Arrived at Job',
  'START_JOB': 'Started Work',
  'FINISH_JOB': 'Finished Work',
  'LEAVE_JOB': 'Left Job',
  'TRAVEL_TO_NEXT': 'Traveling',
  'RETURN_SHOP': 'Returned to Shop',
  'END_DAY': 'Ended Day'
};

// Format duration for display
const formatDuration = (minutes) => {
  if (!minutes && minutes !== 0) return '';
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
};

// Format time from ISO string
const formatTime = (isoString) => {
  if (!isoString) return '';
  try {
    return format(parseISO(isoString), 'h:mm a');
  } catch {
    return '';
  }
};

export default function MyDayTimeline() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, token } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [deleting, setDeleting] = useState(null); // Track which event ID is being deleted
  const [timeline, setTimeline] = useState(null);
  const [selectedDate, setSelectedDate] = useState(
    searchParams.get('date') || format(new Date(), 'yyyy-MM-dd')
  );
  const [assignedJobs, setAssignedJobs] = useState([]);
  
  // Correction dialog state
  const [showCorrectionDialog, setShowCorrectionDialog] = useState(false);
  const [correctionData, setCorrectionData] = useState({
    event_type: '',
    job_id: '',
    timestamp: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, [selectedDate]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Get timezone offset in minutes (e.g., -360 for UTC-6)
      const tzOffset = new Date().getTimezoneOffset();
      
      const [timelineRes, jobsRes] = await Promise.all([
        axios.get(`${API}/api/time-events/timeline/${selectedDate}?tz_offset=${tzOffset}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/time-events/my-assigned-jobs`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setTimeline(timelineRes.data);
      setAssignedJobs(jobsRes.data || []);
    } catch (error) {
      console.error('Failed to load timeline:', error);
      toast.error('Failed to load timeline');
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (direction) => {
    const current = parseISO(selectedDate);
    const newDate = direction === 'next' ? addDays(current, 1) : subDays(current, 1);
    const newDateStr = format(newDate, 'yyyy-MM-dd');
    setSelectedDate(newDateStr);
    setSearchParams({ date: newDateStr });
  };

  const handleDeleteLastEvent = async () => {
    if (!confirm('Delete the most recent event?')) return;
    
    setDeleting(true);
    try {
      const tzOffset = new Date().getTimezoneOffset();
      const response = await axios.delete(`${API}/api/time-events/last?tz_offset=${tzOffset}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Deleted: ${response.data.deleted_event_type.replace(/_/g, ' ')}`);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete event');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteEvent = async (eventId, eventType) => {
    if (!confirm(`Delete "${EVENT_LABELS[eventType] || eventType}"? This cannot be undone.`)) return;
    
    setDeleting(eventId); // Show loading on this specific button
    try {
      const tzOffset = new Date().getTimezoneOffset();
      await axios.delete(`${API}/api/time-events/${eventId}?tz_offset=${tzOffset}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Deleted: ${EVENT_LABELS[eventType] || eventType}`);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete event');
    } finally {
      setDeleting(null);
    }
  };

  const handleAddCorrection = async () => {
    if (!correctionData.event_type || !correctionData.timestamp) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Validate job selection for job-related events
    const jobEvents = ['ARRIVE_JOB', 'START_JOB', 'FINISH_JOB', 'LEAVE_JOB'];
    if (jobEvents.includes(correctionData.event_type) && !correctionData.job_id) {
      toast.error('Please select a job for this event type');
      return;
    }
    
    try {
      const eventData = {
        event_type: correctionData.event_type,
        job_id: correctionData.job_id || null,
        timestamp: new Date(`${selectedDate}T${correctionData.timestamp}`).toISOString(),
        notes: correctionData.notes || 'Manual correction'
      };
      
      await axios.post(`${API}/api/time-events/correction`, eventData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Correction added');
      setShowCorrectionDialog(false);
      setCorrectionData({ event_type: '', job_id: '', timestamp: '', notes: '' });
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add correction');
    }
  };

  const handleSendToTimesheet = async () => {
    setSending(true);
    try {
      // Get timezone offset in minutes
      const tzOffset = new Date().getTimezoneOffset();
      
      const response = await axios.post(
        `${API}/api/time-events/send-to-timesheet?date=${selectedDate}&tz_offset=${tzOffset}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const entries = response.data.entries || [];
      const created = entries.filter(e => e.action === 'created').length;
      const updated = entries.filter(e => e.action === 'updated').length;
      
      let message = '';
      if (created > 0) message += `Created ${created} entries. `;
      if (updated > 0) message += `Updated ${updated} entries.`;
      
      toast.success(message || 'Timesheet updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send to timesheet');
    } finally {
      setSending(false);
    }
  };

  // Compute "today" consistently using the same timezone offset as the backend
  // This ensures delete/edit buttons show when viewing "today" in the user's timezone
  const getUserLocalToday = () => {
    const now = new Date();
    // Format as local date using the same timezone the browser uses
    return format(now, 'yyyy-MM-dd');
  };
  
  const isCurrentDay = selectedDate === getUserLocalToday();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 pb-44">
      {/* Header */}
      <div className="bg-slate-800 text-white p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/field-time-tracker')}
              className="text-white hover:bg-slate-700"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold">My Day Timeline</h1>
              <p className="text-slate-300 text-sm">Review your tracked time</p>
            </div>
          </div>
          
          {/* Pending Sync Badge */}
          {timeline?.pending_sync_count > 0 && (
            <Badge className="bg-amber-500 text-white">
              <WifiOff className="w-3 h-3 mr-1" />
              {timeline.pending_sync_count} pending
            </Badge>
          )}
        </div>
        
        {/* Date Navigation */}
        <div className="flex items-center justify-between bg-slate-700 rounded-lg p-2">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => handleDateChange('prev')}
            className="text-white hover:bg-slate-600"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          
          <div className="text-center">
            <p className="font-semibold">
              {isCurrentDay ? 'Today' : format(parseISO(selectedDate), 'EEEE')}
            </p>
            <p className="text-sm text-slate-300">
              {format(parseISO(selectedDate), 'MMM d, yyyy')}
            </p>
          </div>
          
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => handleDateChange('next')}
            disabled={isCurrentDay}
            className="text-white hover:bg-slate-600 disabled:opacity-50"
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="p-4 -mt-2">
        <div className="grid grid-cols-4 gap-2">
          <Card className="shadow-sm">
            <CardContent className="p-3 text-center">
              <Home className="w-5 h-5 mx-auto text-blue-500 mb-1" />
              <p className="text-lg font-bold text-slate-900">
                {formatDuration(timeline?.durations?.shop_time || 0)}
              </p>
              <p className="text-xs text-slate-500">Shop</p>
            </CardContent>
          </Card>
          
          <Card className="shadow-sm">
            <CardContent className="p-3 text-center">
              <Truck className="w-5 h-5 mx-auto text-amber-500 mb-1" />
              <p className="text-lg font-bold text-slate-900">
                {formatDuration(timeline?.durations?.travel_time || 0)}
              </p>
              <p className="text-xs text-slate-500">Travel</p>
            </CardContent>
          </Card>
          
          <Card className="shadow-sm">
            <CardContent className="p-3 text-center">
              <MapPin className="w-5 h-5 mx-auto text-green-500 mb-1" />
              <p className="text-lg font-bold text-slate-900">
                {formatDuration(timeline?.durations?.on_job_time || 0)}
              </p>
              <p className="text-xs text-slate-500">On Job</p>
            </CardContent>
          </Card>
          
          <Card className="shadow-sm">
            <CardContent className="p-3 text-center">
              <Clock className="w-5 h-5 mx-auto text-purple-500 mb-1" />
              <p className="text-lg font-bold text-slate-900">
                {formatDuration(timeline?.durations?.total_time || 0)}
              </p>
              <p className="text-xs text-slate-500">Total</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Warnings */}
      {timeline?.warnings?.length > 0 && (
        <div className="px-4 mb-4">
          <Card className="border-amber-300 bg-amber-50">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <span className="font-semibold text-amber-800">
                  {timeline.warnings.length} issue{timeline.warnings.length > 1 ? 's' : ''} detected
                </span>
              </div>
              <div className="space-y-1">
                {timeline.warnings.map((warning, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <AlertCircle className={`w-4 h-4 ${
                      warning.severity === 'error' ? 'text-red-500' : 
                      warning.severity === 'warning' ? 'text-amber-500' : 'text-blue-500'
                    }`} />
                    <span className="text-slate-700">{warning.message}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Timeline */}
      <div className="px-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center justify-between">
              <span>Timeline</span>
              <Badge variant="outline">{timeline?.event_count || 0} events</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {timeline?.events?.length === 0 ? (
              <div className="text-center py-8">
                <Clock className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500">No events recorded</p>
                <Button 
                  className="mt-4 bg-amber-500 hover:bg-amber-600"
                  onClick={() => navigate('/field-time-tracker')}
                >
                  <Timer className="w-4 h-4 mr-2" />
                  Start Tracking
                </Button>
              </div>
            ) : (
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200" />
                
                {/* Events */}
                <div className="space-y-4">
                  {timeline.events.map((event, index) => {
                    const Icon = EVENT_ICONS[event.event_type] || Clock;
                    const color = EVENT_COLORS[event.event_type] || 'bg-slate-500';
                    
                    return (
                      <div key={event.id} className="relative flex items-start gap-3 pl-2 group">
                        {/* Icon */}
                        <div className={`relative z-10 w-6 h-6 rounded-full ${color} flex items-center justify-center flex-shrink-0`}>
                          <Icon className="w-3 h-3 text-white" />
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0 pb-4">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="font-medium text-slate-900">
                                {event.friendly_label}
                                {event.is_corrected && (
                                  <Badge variant="outline" className="ml-2 text-xs">
                                    Corrected
                                  </Badge>
                                )}
                              </p>
                              
                              {event.job_number && (
                                <Badge className="mt-1 bg-slate-100 text-slate-700 text-xs">
                                  {event.job_number}
                                  {event.job_title && ` - ${event.job_title.slice(0, 20)}...`}
                                </Badge>
                              )}
                              
                              {event.has_gps && (
                                <span className="text-xs text-green-600 flex items-center gap-1 mt-1">
                                  <Navigation className="w-3 h-3" />
                                  GPS saved
                                </span>
                              )}
                            </div>
                            
                            <div className="text-right flex-shrink-0 flex items-start gap-2">
                              <div>
                                <p className="font-medium text-slate-900">
                                  {formatTime(event.timestamp)}
                                </p>
                                {event.duration_from_prev && (
                                  <Badge variant="outline" className="text-xs mt-1">
                                    +{formatDuration(event.duration_from_prev)}
                                  </Badge>
                                )}
                              </div>
                              
                              {/* Delete button for individual event - only show for today */}
                              {isCurrentDay && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-red-400 hover:text-red-600 hover:bg-red-50 opacity-50 group-hover:opacity-100"
                                  onClick={() => handleDeleteEvent(event.id, event.event_type)}
                                  disabled={deleting === event.id}
                                  data-testid={`delete-event-${event.id}`}
                                >
                                  {deleting === event.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="w-4 h-4" />
                                  )}
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Action Buttons - Fixed footer above mobile nav */}
      <div className="fixed bottom-20 left-0 right-0 bg-white border-t border-slate-200 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] p-3 z-30">
        <div className="flex gap-2 max-w-lg mx-auto px-2">
          {isCurrentDay && (
            <>
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowCorrectionDialog(true)}
                data-testid="add-correction-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Event
              </Button>
              
              <Button
                variant="outline"
                className="text-red-600 border-red-300 hover:bg-red-50"
                onClick={handleDeleteLastEvent}
                disabled={deleting || !timeline?.events?.length}
                data-testid="delete-last-btn"
              >
                {deleting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
              </Button>
            </>
          )}
          
          <Button
            className="flex-1 bg-green-600 hover:bg-green-700"
            onClick={handleSendToTimesheet}
            disabled={sending || !timeline?.events?.length}
            data-testid="send-to-timesheet-btn"
          >
            {sending ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            Send to Timesheet
          </Button>
        </div>
      </div>

      {/* Correction Dialog */}
      <Dialog open={showCorrectionDialog} onOpenChange={setShowCorrectionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Correction Event</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Event Type *</Label>
              <Select 
                value={correctionData.event_type} 
                onValueChange={(v) => setCorrectionData(prev => ({ ...prev, event_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select event type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="START_DAY">Started Day</SelectItem>
                  <SelectItem value="LEAVE_SHOP">Left Shop</SelectItem>
                  <SelectItem value="ARRIVE_JOB">Arrived at Job</SelectItem>
                  <SelectItem value="START_JOB">Started Work</SelectItem>
                  <SelectItem value="FINISH_JOB">Finished Work</SelectItem>
                  <SelectItem value="LEAVE_JOB">Left Job</SelectItem>
                  <SelectItem value="RETURN_SHOP">Returned to Shop</SelectItem>
                  <SelectItem value="END_DAY">Ended Day</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {['ARRIVE_JOB', 'START_JOB', 'FINISH_JOB', 'LEAVE_JOB'].includes(correctionData.event_type) && (
              <div>
                <Label>Job *</Label>
                <Select 
                  value={correctionData.job_id} 
                  onValueChange={(v) => setCorrectionData(prev => ({ ...prev, job_id: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select job" />
                  </SelectTrigger>
                  <SelectContent>
                    {assignedJobs.map(job => (
                      <SelectItem key={job.id} value={job.id}>
                        {job.job_number} - {job.service_address?.slice(0, 30)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div>
              <Label>Time *</Label>
              <TimeInput
                value={correctionData.timestamp}
                onChange={(val) => setCorrectionData(prev => ({ ...prev, timestamp: val }))}
              />
            </div>
            
            <div>
              <Label>Notes (optional)</Label>
              <Input
                placeholder="Reason for correction"
                value={correctionData.notes}
                onChange={(e) => setCorrectionData(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCorrectionDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddCorrection} className="bg-amber-500 hover:bg-amber-600">
              Add Correction
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
